import React from "react";
import ProjectFieldFileControls, {
  ProjectSiteWalkFilesAppendControls,
  fieldPathLooksFileLike,
  shouldOfferProjectFileEditing,
} from "./ProjectFieldFileControls";
import { isProjectFieldFileContentUrl, openAuthenticatedProjectFieldFile } from "./projectFieldFileAuth";

const URL_RE = /^https?:\/\//i;

export function humanizeFieldKey(k) {
  return String(k)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatPrimitive(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return String(v);
}

function isPlainObject(x) {
  return x != null && typeof x === "object" && !Array.isArray(x);
}

function objectKeysConsistent(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  if (!arr.every(isPlainObject)) return null;
  const keys = Object.keys(arr[0]).sort().join("|");
  const same = arr.every((o) => Object.keys(o).sort().join("|") === keys);
  return same ? Object.keys(arr[0]) : null;
}

function pathEndsWithSiteWalkFiles(pathPrefix) {
  return (
    Array.isArray(pathPrefix) &&
    pathPrefix.length >= 2 &&
    String(pathPrefix[pathPrefix.length - 1] || "").toLowerCase() === "site_walk_files"
  );
}

function isSiteWalkFilesTablePath(pathPrefix, tableKeys) {
  return Boolean(
    tableKeys &&
      tableKeys.includes("site_walk_file") &&
      pathEndsWithSiteWalkFiles(pathPrefix),
  );
}

function PayloadLink({ href, className, children }) {
  const t = String(href || "").trim();
  if (isProjectFieldFileContentUrl(t)) {
    return (
      <a
        href={t}
        className={className}
        onClick={(e) => {
          e.preventDefault();
          void openAuthenticatedProjectFieldFile(t);
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <a href={t} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

/**
 * Renders ACF / JSON payloads as definition lists, tables, and text (no raw JSON dump).
 *
 * @param {object} [props.fileEditing] — when set on a domain root, enables file upload on matching fields.
 * @param {string} [props.pathPrefix] — JSON path under `acf_groups_json` (e.g. [groupKey, nested...]).
 */
export default function ProjectPayloadView({ data, depth = 0, pathPrefix = [], fileEditing = null }) {
  const maxDepth = 10;
  if (depth > maxDepth) {
    return <span className="project-detail-muted">…</span>;
  }

  if (data === null || data === undefined) {
    const showFileUiWhenEmpty =
      fileEditing &&
      shouldOfferProjectFileEditing(pathPrefix) &&
      fieldPathLooksFileLike(pathPrefix, "");
    if (showFileUiWhenEmpty) {
      return (
        <div className="project-detail-string-block">
          <span className="project-detail-empty">—</span>
          <ProjectFieldFileControls
            projectId={fileEditing.projectId}
            fieldPath={pathPrefix}
            value=""
            getAuthHeaders={fileEditing.getAuthHeaders}
            onUpdated={fileEditing.onUpdated}
          />
        </div>
      );
    }
    return <span className="project-detail-empty">—</span>;
  }

  if (typeof data === "string") {
    const t = data.trim();
    const showFileUi =
      fileEditing &&
      shouldOfferProjectFileEditing(pathPrefix) &&
      fieldPathLooksFileLike(pathPrefix, data);
    if (URL_RE.test(t)) {
      const short = t.length > 96 ? `${t.slice(0, 93)}…` : t;
      return (
        <div className="project-detail-string-block">
          <PayloadLink href={t} className="project-detail-link">
            {short}
          </PayloadLink>
          {showFileUi ? (
            <ProjectFieldFileControls
              projectId={fileEditing.projectId}
              fieldPath={pathPrefix}
              value={data}
              getAuthHeaders={fileEditing.getAuthHeaders}
              onUpdated={fileEditing.onUpdated}
            />
          ) : null}
        </div>
      );
    }
    return (
      <div className="project-detail-string-block">
        <span className="project-detail-text">{data}</span>
        {showFileUi ? (
          <ProjectFieldFileControls
            projectId={fileEditing.projectId}
            fieldPath={pathPrefix}
            value={data}
            getAuthHeaders={fileEditing.getAuthHeaders}
            onUpdated={fileEditing.onUpdated}
          />
        ) : null}
      </div>
    );
  }

  if (typeof data === "number" || typeof data === "boolean") {
    return <span className="project-detail-text">{formatPrimitive(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      if (fileEditing && pathEndsWithSiteWalkFiles(pathPrefix)) {
        return (
          <div className="project-site-walk-files-wrap">
            <span className="project-detail-empty">No files yet</span>
            <ProjectSiteWalkFilesAppendControls
              projectId={fileEditing.projectId}
              fieldPath={pathPrefix}
              getAuthHeaders={fileEditing.getAuthHeaders}
              onUpdated={fileEditing.onUpdated}
            />
          </div>
        );
      }
      return <span className="project-detail-empty">—</span>;
    }

    const tableKeys = objectKeysConsistent(data);
    if (tableKeys && tableKeys.length) {
      const siteWalkTable = isSiteWalkFilesTablePath(pathPrefix, tableKeys);
      const tableFileEditing = siteWalkTable && fileEditing ? fileEditing : null;
      return (
        <div className="project-site-walk-files-wrap">
          <div className="project-detail-table-wrap">
            <table className="project-detail-table">
              <thead>
                <tr>
                  {tableKeys.map((k) => (
                    <th key={k}>{humanizeFieldKey(k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    {tableKeys.map((k) => (
                      <td key={k}>
                        <ProjectPayloadView
                          data={row[k]}
                          depth={depth + 1}
                          pathPrefix={[...pathPrefix, i, k]}
                          fileEditing={tableFileEditing}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {siteWalkTable && fileEditing ? (
            <ProjectSiteWalkFilesAppendControls
              projectId={fileEditing.projectId}
              fieldPath={pathPrefix}
              getAuthHeaders={fileEditing.getAuthHeaders}
              onUpdated={fileEditing.onUpdated}
            />
          ) : null}
        </div>
      );
    }

    if (data.every((x) => x == null || ["string", "number", "boolean"].includes(typeof x))) {
      return (
        <span className="project-detail-text">
          {data.map((x) => formatPrimitive(x)).join(", ")}
        </span>
      );
    }

    return (
      <ul className="project-detail-nested-list">
        {data.map((item, i) => (
          <li key={i} className="project-detail-nested-item">
            <ProjectPayloadView
              data={item}
              depth={depth + 1}
              pathPrefix={[...pathPrefix, i]}
              fileEditing={null}
            />
          </li>
        ))}
      </ul>
    );
  }

  if (isPlainObject(data)) {
    const entries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) return <span className="project-detail-empty">—</span>;
    return (
      <dl className="project-detail-field-grid">
        {entries.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="project-detail-field-label">{humanizeFieldKey(k)}</dt>
            <dd className="project-detail-field-value">
              <ProjectPayloadView
                data={v}
                depth={depth + 1}
                pathPrefix={[...pathPrefix, k]}
                fileEditing={fileEditing}
              />
            </dd>
          </React.Fragment>
        ))}
      </dl>
    );
  }

  return <span className="project-detail-text">{formatPrimitive(data)}</span>;
}
